/**
 * Email-field helpers shared across every login / sign-up form.
 *
 * Two deliberately separate concerns:
 *  - {@link sanitizeEmailInput} — an *input guard*: strip what can never
 *    belong in an address (whitespace + characters outside the RFC 5321
 *    unquoted set) as the user types, so illegal characters never land in
 *    the field in the first place.
 *  - {@link isValidEmailFormat} — a *shape check*: permissive
 *    RFC-5321-ish "looks like an email" test for inline validation. A UX
 *    hint, not a security control.
 */

// Anything NOT permitted in an unquoted email address — the local part's
// dot-atom set, the domain, and the `@` separator. Whitespace, control
// characters, commas, quotes, brackets, parentheses and non-ASCII all
// match here and get stripped. Global flag removes every offender, not
// just the first.
const DISALLOWED_EMAIL_CHARS = /[^A-Za-z0-9.!#$%&'*+/=?^_`{|}~@-]/g

/**
 * Remove every character that can't legally appear in an email address.
 * Idempotent — a clean address passes through unchanged. Wire it to the
 * field's `onChange` so pasted or typed spaces and illegal characters
 * never stick.
 */
export function sanitizeEmailInput(raw: string): string {
  return raw.replace(DISALLOWED_EMAIL_CHARS, '')
}

// Permissive RFC-5321-ish — covers ~all real addresses without trying to
// reproduce the full grammar.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when `value` has the shape of a real email address. */
export function isValidEmailFormat(value: string): boolean {
  return EMAIL_FORMAT.test(value)
}
